import { useMemo, useState } from "react";

import {
  Alert,
  Button,
  Combobox,
  FormField,
  LoadingSkeleton,
  Modal,
  TextInput,
  useToast,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import {
  useDispatchOptionsQuery,
  useDispatchShipmentMutation,
} from "@/modules/orders/services/useShipment";

type Props = {
  storeSlug: string;
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
};

export function BostaDispatchSheet({
  storeSlug,
  orderId,
  isOpen,
  onClose,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const toast = useToast();

  const { options, isLoading, error } = useDispatchOptionsQuery({
    storeSlug,
    orderId,
    enabled: isOpen,
  });
  const { mutateAsync: dispatch, isPending } = useDispatchShipmentMutation();

  // Prefill comes from the server suggestion; `null` means "not touched by
  // the user yet" so the suggestion applies without any state-syncing effect.
  // The parent mounts this sheet only while open, so closing resets state.
  const [pickupOverride, setPickupOverride] = useState<string | null>(null);
  const [cityOverride, setCityOverride] = useState<string | null>(null);
  const [districtOverride, setDistrictOverride] = useState<string | null>(null);
  const [addressOverride, setAddressOverride] = useState<string | null>(null);
  const [secondPhone, setSecondPhone] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pickupLocationId =
    pickupOverride ?? options?.suggestion.pickupLocationId ?? "";
  const addressFirstLine =
    addressOverride ?? options?.suggestion.buyerAddress ?? "";
  const suggestedCityId = options?.suggestion.cityId ?? "";
  const cityId = cityOverride ?? suggestedCityId;
  // The suggested district only applies while the suggested city is selected.
  const districtId =
    districtOverride ??
    (cityId && cityId === suggestedCityId
      ? (options?.suggestion.districtId ?? "")
      : "");

  const cityLabel = (c: { name: string; nameAr: string }) =>
    locale === "ar" ? c.nameAr || c.name : c.name || c.nameAr;

  const districtsForCity = useMemo(() => {
    if (!options || !cityId) return [];
    return options.districts.filter((d) => d.cityId === cityId);
  }, [options, cityId]);

  const pickupMissing = options && options.pickupLocations.length === 0;
  const canSubmit =
    !isPending &&
    Boolean(pickupLocationId) &&
    Boolean(cityId) &&
    Boolean(districtId) &&
    Boolean(addressFirstLine.trim());

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      await dispatch({
        storeSlug,
        orderId,
        pickupLocationId,
        cityId,
        districtId,
        addressFirstLine: addressFirstLine.trim(),
        secondPhone: secondPhone.trim() || undefined,
      });
      toast.success(t(keys.shipment.dispatchedNotice));
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      elevated
      title={t(keys.shipment.sheetTitle)}
      closeLabel={t(keys.common.close)}
      footerActions={
        <Button
          variant="primary"
          size="md"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {isPending ? t(keys.common.loading) : t(keys.shipment.submitAction)}
        </Button>
      }
    >
      <div className="mx-auto flex w-full max-w-[520px] flex-col gap-5">
        <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
          {t(keys.shipment.sheetIntro)}
        </p>

        {error ? (
          <Alert
            message={
              error instanceof Error ? error.message : t(keys.errors.generic)
            }
          />
        ) : null}

        {submitError ? <Alert message={submitError} /> : null}

        {isLoading && !options ? (
          <LoadingSkeleton variant="card" rows={4} />
        ) : pickupMissing ? (
          <Alert message={t(keys.shipment.noPickupLocations)} />
        ) : options ? (
          <div className="flex flex-col gap-4">
            <FormField
              label={t(keys.shipment.pickupLocationLabel)}
              name="pickupLocationId"
            >
              <Combobox
                options={options.pickupLocations.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                value={pickupLocationId || null}
                onChange={(value) => setPickupOverride(value ?? "")}
                aria-label={t(keys.shipment.pickupLocationLabel)}
              />
            </FormField>

            <FormField label={t(keys.shipment.cityLabel)} name="cityId">
              <Combobox
                options={options.cities.map((c) => ({
                  value: c.id,
                  label: cityLabel(c),
                }))}
                value={cityId || null}
                onChange={(value) => {
                  setCityOverride(value ?? "");
                  // District must belong to the selected city.
                  setDistrictOverride("");
                }}
                aria-label={t(keys.shipment.cityLabel)}
              />
            </FormField>

            <FormField
              label={t(keys.shipment.districtLabel)}
              name="districtId"
            >
              <Combobox
                options={districtsForCity.map((d) => ({
                  value: d.id,
                  label: cityLabel(d),
                }))}
                value={districtId || null}
                onChange={(value) => setDistrictOverride(value ?? "")}
                disabled={!cityId}
                placeholder={
                  !cityId
                    ? t(keys.shipment.pickCityFirst)
                    : districtsForCity.length === 0
                      ? t(keys.shipment.noDistricts)
                      : undefined
                }
                aria-label={t(keys.shipment.districtLabel)}
              />
            </FormField>

            <FormField label={t(keys.shipment.addressLabel)} name="address">
              <TextInput
                name="address"
                required
                value={addressFirstLine}
                onChange={(e) => setAddressOverride(e.target.value)}
              />
            </FormField>

            <FormField
              label={t(keys.shipment.secondPhoneLabel)}
              name="secondPhone"
            >
              <TextInput
                name="secondPhone"
                type="tel"
                dir="ltr"
                value={secondPhone}
                onChange={(e) =>
                  setSecondPhone(e.target.value.replace(/\s+/g, ""))
                }
              />
            </FormField>

            {/* COD amount always visible — even 0 is a meaningful
                confirmation that the order is fully prepaid and the
                courier shouldn't collect cash. */}
            <div
              className={`flex items-center justify-between rounded-lg border px-3 py-3 ${
                options.suggestion.codAmount > 0
                  ? "border-[color:var(--warning-500)]/40 bg-[color:var(--warning-500)]/10"
                  : "border-[color:var(--success-500)]/40 bg-[color:var(--success-500)]/10"
              }`}
            >
              <span className="text-xs font-semibold text-[color:var(--text-secondary)]">
                {t(keys.shipment.codLabel)}
              </span>
              <span className="font-mono text-base font-bold text-[color:var(--text-primary)]">
                {options.suggestion.codAmount > 0
                  ? options.suggestion.codAmount.toFixed(2)
                  : "0.00"}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
